import React, { useState } from 'react';
import { ArrowRight, Play, Pause, Settings, ChevronDown, ChevronUp, Zap } from 'lucide-react';

const FlowNode = ({ node, isLast }) => {
  const getNodeColor = (type) => {
    const colors = {
      trigger: 'bg-purple-100 border-purple-300 text-purple-700',
      action: 'bg-blue-100 border-blue-300 text-blue-700',
      condition: 'bg-yellow-100 border-yellow-300 text-yellow-700',
      output: 'bg-green-100 border-green-300 text-green-700',
    };
    return colors?.[node?.type] || colors?.action;
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium ${getNodeColor(node?.type)}`}>
        <node.icon className="w-3.5 h-3.5" />
        <span>{node?.label}</span>
      </div>
      {!isLast && <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
    </div>
  );
};

const WorkflowCanvas = ({ workflows }) => {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev?.[id] }));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100">
        <Zap className="w-5 h-5 text-purple-600" />
        <h2 className="text-base font-semibold text-gray-900">Automated Workflow Canvas</h2>
        <span className="ml-auto text-xs text-gray-400">{workflows?.length} active workflows</span>
      </div>
      <div className="divide-y divide-gray-50">
        {workflows?.map((wf) => (
          <div key={wf?.id} className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${wf?.active ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm font-medium text-gray-900">{wf?.name}</span>
                <span className="text-xs text-gray-400">{wf?.executions} executions today</span>
              </div>
              <div className="flex items-center gap-2">
                <button className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                  {wf?.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                  <Settings className="w-4 h-4" />
                </button>
                <button
                  onClick={() => toggleExpand(wf?.id)}
                  className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
                >
                  {expanded?.[wf?.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {expanded?.[wf?.id] && (
              <div className="mt-3 pl-5">
                <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  {wf?.nodes?.map((node, idx) => (
                    <FlowNode key={idx} node={node} isLast={idx === wf?.nodes?.length - 1} />
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                  <span>Last run: {wf?.lastRun}</span>
                  <span>Avg duration: {wf?.avgDuration}</span>
                  <span className={`font-medium ${wf?.successRate >= 95 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {wf?.successRate}% success
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowCanvas;
